/**
 * "Is this record me?" — used to stop a member editing their own role or
 * permissions (self-escalation).
 *
 * Nothing in the dashboard identified the current user against a member row,
 * so every role-edit affordance was offered on your own account too.
 *
 * The `/auth/profile` envelope is not consistent across the app — some
 * responses are wrapped in `data`, some are not (CurrencyProvider already
 * reads `data.data.store ?? data.store`). Both shapes are accepted here so
 * call sites don't each re-invent the unwrapping.
 */

export interface CurrentUserIdentity {
  id?: string;
  email?: string;
}

interface IdentityLike {
  id?: string | null;
  email?: string | null;
}

function normalizeEmail(email?: string | null): string | undefined {
  const trimmed = (email ?? "").trim().toLowerCase();
  return trimmed || undefined;
}

function normalizeId(id?: string | null): string | undefined {
  const trimmed = (id ?? "").trim();
  return trimmed || undefined;
}

/** Pull `{ id, email }` out of a `/auth/profile` response, wrapped or not. */
export function getCurrentUserIdentity(profile: unknown): CurrentUserIdentity {
  if (!profile || typeof profile !== "object") return {};
  const root = profile as { data?: { user?: IdentityLike }; user?: IdentityLike };
  const user = root.data?.user ?? root.user;
  if (!user || typeof user !== "object") return {};

  const identity: CurrentUserIdentity = {};
  const id = normalizeId(user.id);
  const email = normalizeEmail(user.email);
  if (id) identity.id = id;
  if (email) identity.email = email;
  return identity;
}

/**
 * True when `target` is the signed-in user.
 *
 * Matches on id OR email — deliberately an OR. A false positive over-restricts
 * (the member sees no Edit on their own row, which is the intent anyway); a
 * false negative lets someone change their own permissions. Returns false
 * while the profile is still loading, since the permission check still gates
 * the action and we don't want the UI to flicker into a locked state.
 */
export function isSelf(
  profile: unknown,
  target?: IdentityLike | null,
): boolean {
  if (!target || typeof target !== "object") return false;
  const me = getCurrentUserIdentity(profile);
  if (!me.id && !me.email) return false;

  const targetId = normalizeId(target.id);
  const targetEmail = normalizeEmail(target.email);

  if (me.id && targetId && me.id === targetId) return true;
  if (me.email && targetEmail && me.email === targetEmail) return true;
  return false;
}
